<template>
  <div
    class="card card-height animate-fade-in"
    @dragover.prevent="handleDragOver"
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <div class="card-body message-body-wrapper pb-0" ref="messageBody">
      <div v-for="(message, index) in messages" :key="index" class="mb-4">
        <!-- ユーザーのメッセージ（右）-->
        <div
          v-if="message.is_user"
          class="d-flex justify-content-end pe-3 mb-3"
        >
          <div class="message-wrapper">
            <div class="user-message-bubble">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p
                class="mb-0 text-sm text-dark"
                v-html="parseMarkdown(message.text)"
              ></p>
            </div>
          </div>
        </div>

        <!-- AIのメッセージ（左）-->
        <div v-else class="d-flex justify-content-start mb-3">
          <div class="input-wrapper">
            <div class="bg-white border rounded p-3 shadow-sm">
              <!-- Generated Images -->
              <div
                v-if="message.images && message.images.length > 0"
                class="generated-images"
              >
                <div
                  v-for="(image, index) in message.images"
                  :key="index"
                  class="image-container"
                >
                  <div class="image-wrapper">
                    <img
                      :src="image.url"
                      :alt="image.alt"
                      @load="handleImageLoad($event, image)"
                      ref="generatedImage"
                    />
                    <div class="image-overlay">
                      <div class="image-actions">
                        <button
                          class="btn btn-warning btn-fixed-width"
                          @click="openImageEditor(message.id, image)"
                        >
                          <i class="fas fa-paint-brush me-1"></i>
                          編集
                        </button>
                        <button
                          class="btn btn-info btn-fixed-width"
                          @click="emitDownloadImage(image.url)"
                          title="画像をダウンロード"
                        >
                          <i class="fas fa-cloud-download-alt me-1"></i>
                          ダウンロード
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 入力欄 -->
    <div
      class="position-sticky bottom-0 p-3 pt-2 px-md-5 d-flex justify-content-center"
    >
      <div
        class="w-100 mt-2 gap-3 d-flex flex-column flex-md-row justify-content-center"
      >
        <div
          class="input-group shadow-lg input-text-wrapper input-wrapper-layout align-items-end"
          :class="{ dragging: isDragging }"
        >
          <div class="file-preview-area" v-if="attachedFiles.length > 0">
            <div class="file-list-wrap">
              <div
                class="file-item-wrap"
                v-for="(file, index) in attachedFiles"
                :key="index"
              >
                <div class="file-content">
                  <template v-if="isImageFile(file)">
                    <img
                      class="file-image-preview"
                      :src="getFilePreview(file)"
                      :alt="file.name"
                    />
                  </template>
                  <template v-else>
                    <i class="fas fa-paperclip me-2 text-secondary"></i>
                    <span class="file-name">{{ file.name }}</span>
                  </template>
                </div>
                <button
                  class="btn-remove"
                  @click="removeFile(index)"
                  title="削除"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- ファイル添付ボタン -->
          <label for="file-upload" style="cursor: pointer; margin-right: 8px">
            <i
              class="fas fa-paperclip"
              style="font-size: 18px; color: #555"
            ></i>
          </label>
          <input
            type="file"
            id="file-upload"
            ref="fileInput"
            multiple
            @change="handleInputFileChange"
            style="display: none"
            accept=".jpeg,.jpg,.png"
          />
          <textarea
            :class="[
              'form-control',
              { 'border-0': true, 'shadow-none': true, expanded: focused },
            ]"
            placeholder="作成したい画像のイメージを入力してね！"
            @focus="focused = true"
            @blur="focused = false"
            v-model="userInput"
            @keydown.enter.exact.prevent="submitMessage"
            @keydown.enter.ctrl="insertNewLine"
            @paste="handlePaste"
            maxlength="1000"
          ></textarea>
          <!-- 送信ボタン -->
          <div
            class="btn btn-icon-only btn-icon-send bg-dark text-white rounded-circle"
            @click="submitMessage"
          >
            <i class="far fa-comment-dots" aria-hidden="true"></i>
          </div>
        </div>
        <ChatSelectBox
          v-model="selectedSize"
          :options="sizeOptions"
          label="画像サイズ"
          labelWide="w-100"
        />
      </div>
    </div>
    <ImageEditorModal
      v-if="showImageEditor"
      :id="conversationUniqueId"
      :image="editingImage"
      @close="showImageEditor = false"
      @generate="generatePartialImage"
    />
  </div>
</template>

<script>
import DOMPurify from 'dompurify'
import ImageEditorModal from './ImageEditorModal.vue'
import ChatSelectBox from '@/components/PageLayout/App/ChatSelectBox.vue'

export default {
  name: 'ChatCard',
  components: {
    ChatSelectBox,
    ImageEditorModal,
  },
  emits: [
    'remove-file',
    'handle-dragover',
    'handle-drop',
    'handle-download-image',
    'handle-selected-size',
    'upload-files',
    'send-message',
    'generate-partial-image',
  ],
  props: {
    messages: {
      type: Array,
      required: true,
    },
    attachedFiles: {
      type: Array,
      required: true,
    },
    size: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
    },
    parseMarkdown: {
      type: Function,
      required: true,
    },
    handleImageLoad: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      focused: false,
      isDragging: false,
      dragCounter: 0,
      newMessage: '',
      userInput: '',
      selectedSize: this.size,
      sizeOptions: this.options,
      showImageEditor: false,
      conversationUniqueId: null,
      editingImage: null,
    }
  },
  methods: {
    handleDragEnter() {
      this.dragCounter++
      this.isDragging = true
    },
    handleDragLeave() {
      this.dragCounter--
      if (this.dragCounter === 0) {
        this.isDragging = false
      }
    },
    handleDrop(event) {
      this.dragCounter = 0
      this.isDragging = false
      this.$emit('handle-drop', event)
      this.$refs.fileInput.value = ''
    },
    handleDragOver(event) {
      this.$emit('handle-dragover', event)
      event.preventDefault()
    },
    handleInputFileChange(event) {
      this.$emit('upload-files', event)
      this.$refs.fileInput.value = ''
    },
    handlePaste(event) {
      const items = event.clipboardData?.items
      if (!items) return

      const files = []

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault()

          const file = item.getAsFile()
          if (!file) continue

          const ext = file.type.split('/')[1] || 'png'
          const fileName = `screenshot-${Date.now()}.${ext}`

          const newFile = new File([file], fileName, {
            type: file.type,
          })

          files.push(newFile)
        }
      }

      if (files.length === 0) return

      // ここがポイント：input[type=file]風のイベントを作る
      const pseudoEvent = {
        target: {
          files: files,
        },
      }

      // 既存ロジックに流す
      this.handleInputFileChange(pseudoEvent)
    },
    removeFile(index) {
      this.$emit('remove-file', index)
    },
    isImageFile(file) {
      return file.type.startsWith('image/')
    },
    getFilePreview(file) {
      return URL.createObjectURL(file)
    },
    getFileExtension(file) {
      return file.name.split('.').pop().toUpperCase()
    },
    sanitize(html) {
      return DOMPurify.sanitize(html)
    },
    submitMessage() {
      const trimmed = this.userInput.trim()
      if (trimmed) {
        this.$emit('send-message', trimmed)
        this.userInput = ''
      }
    },
    insertNewLine(event) {
      const textarea = event.target
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      this.userInput =
        this.userInput.substring(0, start) +
        '\n' +
        this.userInput.substring(end)
      this.$nextTick(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 1
      })
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messageBody
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    emitDownloadImage(url) {
      this.$emit('handle-download-image', url)
    },
    openImageEditor(id, image) {
      this.conversationUniqueId = id
      this.editingImage = image
      this.showImageEditor = true
    },
    generatePartialImage(payload) {
      this.showImageEditor = false
      this.$emit('generate-partial-image', payload)
    },
  },
  watch: {
    messages: {
      async handler() {
        // Vueの描画完了を待つ
        await this.$nextTick()
        this.scrollToBottom()
      },
      deep: true,
      immediate: true,
    },
    selectedSize(newVal) {
      this.$emit('handle-selected-size', newVal)
    },
  },
  mounted() {
    this.scrollToBottom()
  },
}
</script>
